import { reactive, readonly } from 'vue';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { BASE_URL } from '@/constants/app-config';

export type ConnectionToastKind = 'slow' | 'offline' | 'online';

const FAST_MS = 1000;
const SLOW_MS = 2000;
const SLOW_TOAST_MS = 2500;
const BACK_ONLINE_TOAST_MS = 3500;
const SLOW_COOLDOWN_MS = 20000;
const SLOW_WINDOW_MS = 12000;
const SEVERAL_SLOW_THRESHOLD = 2;
const HEALTH_INTERVAL_MS = 4000;
const HEALTH_TIMEOUT_MS = 5000;

interface ConnectionToastState {
  visible: boolean;
  kind: ConnectionToastKind | null;
  message: string;
}

const state = reactive<ConnectionToastState>({
  visible: false,
  kind: null,
  message: '',
});

export const connectionToastState = readonly(state);

let nextRequestId = 1;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let healthTimer: ReturnType<typeof setInterval> | null = null;
let healthInFlight = false;
let started = false;
let lastSlowShownAt = 0;
let wasOffline = false;

interface TrackedRequest {
  timer: ReturnType<typeof setTimeout>;
  isSlow: boolean;
  isLongRunning: boolean;
}

const inFlight = new Map<number, TrackedRequest>();
const recentSlowAt: number[] = [];

function isLongRunningRequest(config?: AxiosRequestConfig): boolean {
  if (!config) {
    return false;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    return true;
  }
  const url = `${config.baseURL ?? ''}${config.url ?? ''}`.toLowerCase();
  return url.includes('/upload') || url.includes('/import');
}

function isNetworkFailure(error?: AxiosError): boolean {
  if (!error) {
    return false;
  }
  if (axios.isCancel(error)) {
    return false;
  }
  return !error.response;
}

function pruneRecentSlow(now: number): void {
  while (recentSlowAt.length && now - recentSlowAt[0] > SLOW_WINDOW_MS) {
    recentSlowAt.shift();
  }
}

function clearHideTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function showToast(kind: ConnectionToastKind, message: string, autoHideMs?: number): void {
  clearHideTimer();
  state.kind = kind;
  state.message = message;
  state.visible = true;

  if (autoHideMs) {
    hideTimer = setTimeout(() => {
      state.visible = false;
      hideTimer = null;
    }, autoHideMs);
  }
}

function hideToast(): void {
  clearHideTimer();
  state.visible = false;
}

function maybeShowSlow(): void {
  if (state.visible && (state.kind === 'offline' || state.kind === 'online')) {
    return;
  }

  const now = Date.now();
  if (now - lastSlowShownAt < SLOW_COOLDOWN_MS) {
    return;
  }

  pruneRecentSlow(now);
  const slowInFlight = [...inFlight.values()].filter((request) => request.isSlow && !request.isLongRunning).length;
  if (slowInFlight + recentSlowAt.length < SEVERAL_SLOW_THRESHOLD) {
    return;
  }

  lastSlowShownAt = now;
  showToast('slow', 'Connection is slow', SLOW_TOAST_MS);
}

function startHealthPolling(): void {
  if (healthTimer) {
    return;
  }
  void checkHealth();
  healthTimer = setInterval(() => {
    void checkHealth();
  }, HEALTH_INTERVAL_MS);
}

function stopHealthPolling(): void {
  if (healthTimer) {
    clearInterval(healthTimer);
    healthTimer = null;
  }
}

async function checkHealth(): Promise<void> {
  if (healthInFlight) {
    return;
  }
  healthInFlight = true;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

  try {
    await fetch(`${BASE_URL}/quiz?page=1`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'include',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    markOnlineFromHealthCheck();
  } catch {
    if (!wasOffline) {
      markOffline();
    }
  } finally {
    window.clearTimeout(timeoutId);
    healthInFlight = false;
  }
}

function markOffline(): void {
  wasOffline = true;
  startHealthPolling();
  showToast('offline', 'You are offline. We will reconnect automatically.');
}

function markOnlineFromHealthCheck(): void {
  stopHealthPolling();
  if (!wasOffline) {
    return;
  }
  wasOffline = false;
  showToast('online', 'Back online', BACK_ONLINE_TOAST_MS);
}

export function trackRequestStart(config: AxiosRequestConfig): number {
  const id = nextRequestId;
  nextRequestId += 1;
  const isLongRunning = isLongRunningRequest(config);

  const timer = setTimeout(() => {
    const tracked = inFlight.get(id);
    if (!tracked) {
      return;
    }
    tracked.isSlow = true;
    maybeShowSlow();
  }, SLOW_MS);

  inFlight.set(id, {
    timer,
    isSlow: false,
    isLongRunning,
  });

  return id;
}

export function trackRequestEnd(requestId: number | undefined, durationMs: number, error?: AxiosError): void {
  if (requestId == null) {
    return;
  }

  const tracked = inFlight.get(requestId);
  if (tracked) {
    clearTimeout(tracked.timer);
    inFlight.delete(requestId);
  }

  if (isNetworkFailure(error)) {
    markOffline();
    return;
  }

  if (wasOffline) {
    markOnlineFromHealthCheck();
    return;
  }

  const isLongRunning = tracked?.isLongRunning ?? false;
  if (isLongRunning || durationMs < FAST_MS) {
    return;
  }

  if (durationMs >= SLOW_MS) {
    recentSlowAt.push(Date.now());
    maybeShowSlow();
  }
}

export function attachAxiosConnectionInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    if (!config.skipConnectionMonitor) {
      config.__connectionStartedAt = Date.now();
      config.__connectionRequestId = trackRequestStart(config);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const config = response.config;
      if (!config.skipConnectionMonitor) {
        const startedAt = config.__connectionStartedAt ?? Date.now();
        trackRequestEnd(config.__connectionRequestId, Date.now() - startedAt);
      }
      return response;
    },
    (error: AxiosError) => {
      const config = error.config;
      if (config && !config.skipConnectionMonitor) {
        const startedAt = config.__connectionStartedAt ?? Date.now();
        trackRequestEnd(config.__connectionRequestId, Date.now() - startedAt, error);
      } else if (!config) {
        if (isNetworkFailure(error)) {
          markOffline();
        }
      }
      return Promise.reject(error);
    }
  );
}

function onBrowserOffline(): void {
  markOffline();
}

function onBrowserOnline(): void {
  if (wasOffline) {
    startHealthPolling();
  }
  void checkHealth();
}

export function startConnectionMonitor(): void {
  if (started || typeof window === 'undefined') {
    return;
  }
  started = true;

  window.addEventListener('offline', onBrowserOffline);
  window.addEventListener('online', onBrowserOnline);

  if (!navigator.onLine) {
    markOffline();
  }
}

export function stopConnectionMonitor(): void {
  if (!started || typeof window === 'undefined') {
    return;
  }
  started = false;
  window.removeEventListener('offline', onBrowserOffline);
  window.removeEventListener('online', onBrowserOnline);
  stopHealthPolling();
  inFlight.forEach((request) => clearTimeout(request.timer));
  inFlight.clear();
  hideToast();
}
